import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { api, ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";

type Parametro = { id: number; clave: string; valor: string; tipoDato: string; descripcion: string | null };

export function Parametros() {
  const { tienePermiso } = useAuth();
  const puedeEditar = tienePermiso("core.parametros:editar");

  const [parametros, setParametros] = useState<Parametro[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [valorEdit, setValorEdit] = useState("");
  const [guardando, setGuardando] = useState(false);

  const [modalAbierto, setModalAbierto] = useState(false);
  const [clave, setClave] = useState("");
  const [valor, setValor] = useState("");
  const [tipoDato, setTipoDato] = useState("texto");
  const [descripcion, setDescripcion] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      setParametros(await api.get("/parametros"));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar los parámetros.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const iniciarEdicion = (p: Parametro) => {
    setEditandoId(p.id);
    setValorEdit(p.valor);
  };

  const guardarEdicion = async (p: Parametro) => {
    setGuardando(true);
    try {
      await api.put(`/parametros/${p.id}`, { valor: valorEdit });
      setEditandoId(null);
      await cargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo guardar el parámetro.");
    } finally {
      setGuardando(false);
    }
  };

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setFormError(null);
    try {
      await api.post("/parametros", { clave, valor, tipoDato, descripcion });
      setModalAbierto(false);
      setClave(""); setValor(""); setTipoDato("texto"); setDescripcion("");
      await cargar();
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "No se pudo crear el parámetro.");
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (p: Parametro) => {
    if (!window.confirm(`¿Eliminar el parámetro "${p.clave}"?`)) return;
    try {
      await api.del(`/parametros/${p.id}`);
      await cargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo eliminar el parámetro.");
    }
  };

  return (
    <Shell titulo="Parámetros del sistema" breadcrumb="Core / Parámetros">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-textmuted">
          Valores base para producción y costeo (SMLV, rendimientos esperados, márgenes) usados por los módulos de Fase 2+.
        </p>
        {puedeEditar && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nuevo parámetro</button>}
      </div>

      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}

      <div className="card overflow-hidden">
        {cargando ? (
          <p className="p-6 text-sm text-textmuted">Cargando...</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-borderc">
                <th className="table-th">Clave</th>
                <th className="table-th">Valor</th>
                <th className="table-th">Tipo</th>
                <th className="table-th">Descripción</th>
                <th className="table-th"></th>
              </tr>
            </thead>
            <tbody>
              {parametros.map((p) => (
                <tr key={p.id} className="border-b border-borderc last:border-0">
                  {/* Antes usaba font-mono + font-semibold (el mismo estilo que
                      el "código" de Catálogos), pero acá se veía como una
                      etiqueta/badge en vez de simple texto (pedido real del
                      usuario: "las claves no me gustan que se vea como
                      label") — se deja como texto normal, sin fuente
                      monoespaciada ni negrilla. */}
                  <td className="table-td text-sm text-slate-700">{p.clave}</td>
                  <td className="table-td">
                    {editandoId === p.id ? (
                      <input className="input-field py-1" value={valorEdit} onChange={(e) => setValorEdit(e.target.value)} autoFocus />
                    ) : (
                      p.valor
                    )}
                  </td>
                  <td className="table-td"><span className="badge bg-slate-100 text-slate-600">{p.tipoDato}</span></td>
                  <td className="table-td text-textmuted">{p.descripcion}</td>
                  <td className="table-td text-right space-x-2">
                    {puedeEditar && editandoId === p.id ? (
                      <>
                        <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => guardarEdicion(p)} disabled={guardando}>Guardar</button>
                        <button className="text-slate-500 hover:underline text-xs font-semibold" onClick={() => setEditandoId(null)}>Cancelar</button>
                      </>
                    ) : puedeEditar ? (
                      <>
                        <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => iniciarEdicion(p)}>Editar</button>
                        <button className="text-red-600 hover:underline text-xs font-semibold" onClick={() => eliminar(p)}>Eliminar</button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">Nuevo parámetro</h3>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Clave</label>
              <input className="input-field font-mono" value={clave} onChange={(e) => setClave(e.target.value)} placeholder="ej. rendimiento_esperado_tilapia_roja" required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Valor</label>
              <input className="input-field" value={valor} onChange={(e) => setValor(e.target.value)} required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo de dato</label>
              <select className="input-field" value={tipoDato} onChange={(e) => setTipoDato(e.target.value)}>
                <option value="texto">texto</option>
                <option value="numero">número</option>
                <option value="porcentaje">porcentaje</option>
                <option value="booleano">booleano</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Descripción</label>
              <input className="input-field" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
            </div>
            {formError && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{formError}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={guardando}>{guardando ? "Guardando..." : "Crear"}</button>
            </div>
          </form>
        </div>
      )}
    </Shell>
  );
}
